import { useId, useState } from 'react';
import type { AccountRow, UitnodigingRow } from '@fleet/shared';
import { keurAccountEmail } from '@fleet/shared';
import { Alert, Badge, Button } from '../../design-system';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Panel } from '../../components/ui/Panel';
import { ApiError } from '../../lib/apiClient';
import {
  useAccounts,
  useNodigUit,
  useStuurOpnieuw,
  useTrekIn,
  useVerwijderAccount,
} from './accounts.api';

/** `2026-08-11T12:00:00.000Z` → `11-08-2026`. */
function datum(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '-'
    : `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}

/** Hoe lang een uitnodiging nog werkt, in mensentaal. */
function resterend(expiresAt: string): string {
  const uren = Math.round((Date.parse(expiresAt) - Date.now()) / 3600000);
  if (uren <= 0) return 'verlopen';
  if (uren === 1) return 'nog 1 uur geldig';
  if (uren < 24) return `nog ${uren} uur geldig`;
  return 'nog een dag geldig';
}

const rij: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
  borderTop: '1px solid #E7E4DA',
};

export function AccountsPanel() {
  const veldId = useId();
  const { data, isLoading } = useAccounts();
  const nodigUit = useNodigUit();
  const opnieuw = useStuurOpnieuw();
  const trekIn = useTrekIn();
  const verwijder = useVerwijderAccount();

  const [email, setEmail] = useState('');
  const [fout, setFout] = useState('');
  const [gelukt, setGelukt] = useState('');
  const [teVerwijderen, setTeVerwijderen] = useState<AccountRow | null>(null);
  const [inTeTrekken, setInTeTrekken] = useState<UitnodigingRow | null>(null);

  /** Dezelfde controle als de server doet, zodat een tikfout niet eerst een
   *  rondje over het netwerk hoeft. De server blijft de baas. */
  const invoerFout = email ? keurAccountEmail(email) : null;

  async function uitnodigen(e: React.FormEvent) {
    e.preventDefault();
    setFout('');
    setGelukt('');
    if (invoerFout) return setFout(invoerFout);
    try {
      await nodigUit.mutateAsync(email);
      setGelukt(`Uitnodiging verstuurd naar ${email.trim().toLowerCase()}.`);
      setEmail('');
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : 'Uitnodigen mislukt');
    }
  }

  async function doe(actie: () => Promise<unknown>, melding: string) {
    setFout('');
    setGelukt('');
    try {
      await actie();
      setGelukt(melding);
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : 'Actie mislukt');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Panel style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111111' }}>
            Iemand toegang geven
          </div>
          <div style={{ fontSize: 12, color: '#888888', marginTop: 4 }}>
            De uitgenodigde krijgt een mail met een link waarmee hij zelf een wachtwoord kiest. Die
            link werkt één dag en kan één keer gebruikt worden.
          </div>
        </div>
        {/* Label en foutmelding staan buiten de rij, zodat het veld en de knop
            in dezelfde flexrij zitten: `stretch` maakt ze dan even hoog. De
            `Input` uit het design system stapelt label, veld en hint in één
            kolom, en daar past een knop die aan het veld vastzit niet in — die
            zou je met een marge op zijn plek moeten duwen en dat schuift zodra
            er een label of hint bij komt of weggaat. Zelfde klassen, andere
            opbouw. */}
        <form onSubmit={uitnodigen}>
          <label className="vs-label" htmlFor={veldId}>
            E-mailadres
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginTop: 8 }}>
            <input
              id={veldId}
              className="vs-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@veerenstael.nl"
              aria-invalid={invoerFout ? 'true' : undefined}
              style={{ flex: 1 }}
            />
            <Button variant="primary" type="submit" disabled={nodigUit.isPending || !email}>
              {nodigUit.isPending ? 'Versturen…' : 'Uitnodigen'}
            </Button>
          </div>
          {invoerFout && (
            <span className="vs-hint vs-hint--error" style={{ display: 'block', marginTop: 8 }}>
              {invoerFout}
            </span>
          )}
        </form>
        {fout && <Alert tone="danger">{fout}</Alert>}
        {gelukt && <Alert tone="success">{gelukt}</Alert>}
      </Panel>

      <Panel>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111111', marginBottom: 6 }}>
          Toegang tot de app{data ? ` (${data.accounts.length})` : ''}
        </div>
        <div style={{ fontSize: 12, color: '#888888' }}>
          Dit staat los van Personen: daar staan de berijders in het wagenpark, hier staat wie kan
          inloggen.
        </div>
        {isLoading && <div style={{ fontSize: 13, color: '#888888', marginTop: 12 }}>Laden…</div>}
        <div style={{ marginTop: 10 }}>
          {data?.accounts.map((a) => (
            <div key={a.id} style={rij}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111111' }}>
                  {a.naam}
                  {a.isZelf && <span style={{ color: '#888888', fontWeight: 400 }}> — jij</span>}
                </div>
                <div style={{ fontSize: 12, color: '#666666' }}>{a.email}</div>
              </div>
              <div style={{ fontSize: 12, color: '#888888' }}>sinds {datum(a.createdAt)}</div>
              <Button
                variant="ghost"
                size="sm"
                disabled={a.isZelf || verwijder.isPending}
                title={a.isZelf ? 'Je kunt je eigen account niet verwijderen' : undefined}
                onClick={() => setTeVerwijderen(a)}
              >
                Verwijderen
              </Button>
            </div>
          ))}
        </div>
      </Panel>

      {!!data?.uitnodigingen.length && (
        <Panel>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111111', marginBottom: 6 }}>
            Openstaande uitnodigingen ({data.uitnodigingen.length})
          </div>
          <div style={{ marginTop: 4 }}>
            {data.uitnodigingen.map((u) => (
              <div key={u.id} style={rij}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111111' }}>{u.email}</div>
                  <div style={{ fontSize: 12, color: '#666666' }}>
                    verstuurd {datum(u.createdAt)}
                    {u.uitgenodigdDoor ? ` door ${u.uitgenodigdDoor}` : ''}
                  </div>
                </div>
                <Badge tone={u.verlopen ? 'danger' : 'warning'}>
                  {u.verlopen ? 'Verlopen' : resterend(u.expiresAt)}
                </Badge>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={opnieuw.isPending}
                  onClick={() =>
                    doe(
                      () => opnieuw.mutateAsync(u.id),
                      `Nieuwe uitnodiging verstuurd naar ${u.email}.`,
                    )
                  }
                >
                  Opnieuw versturen
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setInTeTrekken(u)}>
                  Intrekken
                </Button>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {teVerwijderen && (
        <ConfirmModal
          title="Account verwijderen"
          message={`${teVerwijderen.naam} (${teVerwijderen.email}) kan daarna niet meer inloggen. Een lopende sessie stopt bij de eerstvolgende actie.`}
          confirmLabel="Verwijderen"
          danger
          busy={verwijder.isPending}
          onClose={() => setTeVerwijderen(null)}
          onConfirm={async () => {
            const naam = teVerwijderen.naam;
            await doe(
              () => verwijder.mutateAsync(teVerwijderen.id),
              `${naam} heeft geen toegang meer.`,
            );
            setTeVerwijderen(null);
          }}
        />
      )}

      {inTeTrekken && (
        <ConfirmModal
          title="Uitnodiging intrekken"
          message={`De link in de mail naar ${inTeTrekken.email} werkt daarna niet meer.`}
          confirmLabel="Intrekken"
          danger
          busy={trekIn.isPending}
          onClose={() => setInTeTrekken(null)}
          onConfirm={async () => {
            const adres = inTeTrekken.email;
            await doe(
              () => trekIn.mutateAsync(inTeTrekken.id),
              `Uitnodiging voor ${adres} ingetrokken.`,
            );
            setInTeTrekken(null);
          }}
        />
      )}
    </div>
  );
}
